import api from '@/services/api.service';
import { postExcelImportWithRetry } from '@/utils/excelImportUpload';

function unwrap<T>(res: unknown): T {
  const data = (res as any)?.data ?? res;
  return (data?.data ?? data) as T;
}

export interface OpeningStockBatchLine {
  id: string;
  line_number: number;
  item_code: string;
  item_id?: string | null;
  warehouse_code?: string | null;
  warehouse_id?: string | null;
  quantity: number;
  unit_rate: number;
  uom?: string | null;
  batch_number?: string | null;
  heat_number?: string;
  dimension?: string;
  mtc_document_id?: string | null;
  mtc_document_number?: string | null;
  is_critical?: boolean;
  heat_number_mandatory?: boolean;
  mtc_required_on_receipt?: boolean;
  serial_number?: string | null;
  remarks?: string | null;
  validation_errors?: string[] | null;
  row_status: string;
}

export interface OpeningStockBatch {
  id: string;
  company_id: string;
  department_id?: string | null;
  batch_number: string;
  status: string;
  file_name?: string | null;
  notes?: string | null;
  validated_at?: string | null;
  posted_at?: string | null;
  lines?: OpeningStockBatchLine[];
}

class OpeningStockService {
  async downloadTemplate(): Promise<void> {
    const res = await api.getClient().get('/inventory/opening-stock/template', {
      responseType: 'blob',
    });
    const blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'opening-stock-template.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);
  }

  async listBatches(): Promise<OpeningStockBatch[]> {
    const res = await api.getClient().get('/inventory/opening-stock/batches');
    return unwrap<OpeningStockBatch[]>(res);
  }

  async getBatch(id: string): Promise<OpeningStockBatch> {
    const res = await api.getClient().get(`/inventory/opening-stock/batches/${id}`);
    return unwrap<OpeningStockBatch>(res);
  }

  async upload(file: File, departmentId: string): Promise<OpeningStockBatch> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('department_id', departmentId);
    const res = await postExcelImportWithRetry(api.getClient(), '/inventory/opening-stock/batches/upload', fd, {
      timeout: 120000,
    });
    return unwrap<OpeningStockBatch>(res);
  }

  async updateBatch(
    id: string,
    payload: { department_id?: string | null; notes?: string | null }
  ): Promise<OpeningStockBatch> {
    const res = await api.getClient().patch(`/inventory/opening-stock/batches/${id}`, payload);
    return unwrap<OpeningStockBatch>(res);
  }

  async validate(batchId: string): Promise<OpeningStockBatch> {
    const res = await api.getClient().post(`/inventory/opening-stock/batches/${batchId}/validate`, null, {
      timeout: 300_000,
    });
    return unwrap<OpeningStockBatch>(res);
  }

  async post(batchId: string, allowSecondOpening?: boolean): Promise<OpeningStockBatch> {
    const res = await api.getClient().post(
      `/inventory/opening-stock/batches/${batchId}/post`,
      { allowSecondOpening: !!allowSecondOpening },
      { timeout: 600_000 }
    );
    return unwrap<OpeningStockBatch>(res);
  }

  async cancel(batchId: string): Promise<OpeningStockBatch> {
    const res = await api.getClient().post(`/inventory/opening-stock/batches/${batchId}/cancel`);
    return unwrap<OpeningStockBatch>(res);
  }

  async setLineMtc(
    batchId: string,
    lineId: string,
    mtcDocumentId: string | null
  ): Promise<OpeningStockBatch> {
    const res = await api.getClient().patch(
      `/inventory/opening-stock/batches/${batchId}/lines/${lineId}/mtc`,
      { mtc_document_id: mtcDocumentId }
    );
    return unwrap<OpeningStockBatch>(res);
  }

  async uploadMtcZip(batchId: string, file: File): Promise<{
    batch: OpeningStockBatch;
    matched: Array<{ file: string; line_number: number; item_code: string; heat_number: string; dimension: string }>;
    unmatched: Array<{ file: string; reason: string }>;
    skipped: Array<{ file: string; reason: string }>;
    naming_hint: string;
  }> {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.getClient().post(`/inventory/opening-stock/batches/${batchId}/mtc-zip`, fd, {
      timeout: 600_000,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap(res);
  }
}

export default new OpeningStockService();
